import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { StatusChip, Banner, EmptyState, RowButton } from "../../components/mui";
import { InlineLoading, PageLoading, Ring } from "../../components/loading";
import { useApp } from "../../design/AppContext";
import { apiPostJson } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import { actionPermissions, hasPermission } from "../../utils/permission";
import {
  isActiveStatus,
  fmtCount,
  fmtDuration,
  safeHttpUrl,
  stripHtml,
  ScanDrawer,
  useScanPage,
  useRunHistory,
  useRunReport,
} from "../../components/scan/ScanKit";
/*
 * 問題清單「存了幾筆、少了幾筆」的判斷與 CSV 下載跟 /CodeScan 的報告用同一份，
 * 兩頁講的數字才會一樣（為什麼暫時放在那一頁而不是 ScanKit，見那邊的註解）。
 */
import { coverageBanner, downloadIssuesCsv, hasStoredIssues, issueCoverage } from "../CodeScan/Index";
import {
  MdCheck,
  MdChevronRight,
  MdCircle,
  MdErrorOutline,
  MdExpandMore,
  MdRemove,
  MdWarningAmber,
} from "react-icons/md";

/**
 * 檢測（設計稿 el-pm-template_4.html:1935-2006 #screen-scan）
 *
 * 舊的「專案原碼檢測」與「專案弱點掃描」是兩支各自獨立的頁面，各有一份專案下拉、
 * 一份清單維護、一面 10～11 欄的歷史表格。實際上使用者問的只有一句話：
 * 「這個專案現在乾不乾淨？」所以這一頁把兩邊合成一頁：
 *
 *   · 頂部一個專案下拉（兩邊 Projects 的聯集）；
 *   · 兩張卡各自講一個結論（程式碼檢查／資安掃描），沒有權限的那一張整張不畫；
 *   · 結果先寫結論與下一步，數字逐行寫成句子 —— 不做四格數字牆（設計規則 L1941）；
 *   · 歷史兩邊交錯成一份時間序，只留 4 欄（時間／誰跑的／關鍵數字／結果）。
 *
 * 這三塊用分頁切換（程式碼檢查／資安掃描／以前的檢測紀錄），不再從上到下疊成一長頁：
 * 使用者反映整頁太長，要看歷史得先捲過兩張卡。分頁只畫有內容的那幾個 —— 沒有權限、
 * 專案不在那個模組的範圍、一次紀錄都沒有，那一個分頁就不畫（設計規則 2）。
 * 分頁的缺點是看不到的那一頁有警訊也看不到，所以「沒過」「有高風險」「檢查中」會直接
 * 寫在分頁標籤上，不必點進去才知道那邊出事了。
 *
 * 這一頁刻意只做「看結論、再跑一次、看明細」三件事。維護 Git 連結與掃描網址、
 * 取消或刪除某一次紀錄、下載 ZAP 的 HTML 報告都還在原本的兩支頁面（/CodeScan、
 * /VulnScan）—— 那些功能一個都沒有拿掉，每張卡、歷史分頁與抽屜底下都留了進得去的入口。
 *
 * 資料完全不進 redux，沿用 components/scan/ScanKit.js 的三個 hook，每個各呼叫兩次
 * （base 分別是 "/CodeScan" 與 "/VulnScan"），輪詢也照舊由那三個 hook 自己處理。
 * 沒有新增任何後端端點。
 */

const CODE_BASE = "/CodeScan";
const VULN_BASE = "/VulnScan";

/* 分頁代碼，也是網址上 ?tab= 的值 */
const TAB_CODE = "code";
const TAB_VULN = "vuln";
const TAB_HISTORY = "history";

/* useMemo 的相依要穩定：沒權限、沒資料的那一邊每次 render 都給同一個空陣列，不要寫 [] 字面值 */
const NO_PROJECTS = [];
const NO_RESULTS = [];

/**
 * 風險四階 → 三組 token（同 ScanKit 檔頭的說明）。
 * 高＝--attention、中與低都是 --warn、資訊＝--closed（灰）。
 * 中與低同色是有意的：新 token 裡沒有第四個色階，硬擠一個等於自己發明顏色；
 * 「中風險」「低風險」這幾個字本來就寫在列上，顏色少一階不會讓人看錯。
 *
 * 符號跟著顏色走，不另外發明一套：設計稿裡 attention 一律 ⚠、warn 一律 ！、
 * closed 一律 ―（progress 的 ● 是「正在跑」專用，不能拿來當風險等級）。
 * 這一頁跟 /Today、/Tasks、/Schedule 用同一張對照表，換頁時符號的意思才不會變。
 */
const RISK_TAG = { 3: "attention", 2: "warn", 1: "warn", 0: "closed" };
const RISK_MARK = { 3: <MdWarningAmber />, 2: <MdErrorOutline />, 1: <MdErrorOutline />, 0: <MdRemove /> };

/**
 * 資安掃描卡的四行句子（設計稿 L1970-1972）。
 * advice 是「下一步」，不是資料 —— 數字一律從 API 來，這裡只決定怎麼念。
 */
const RISK_LINES = [
  { key: "high_count", risk: 3, label: "高風險", zero: "無須立即處理的項目。", some: "應優先處理。" },
  { key: "medium_count", risk: 2, label: "中風險", zero: "無。", some: "建議於本月內處理。" },
  { key: "low_count", risk: 1, label: "低風險", zero: "無。", some: "可於下個版本處理。" },
  { key: "info_count", risk: 0, label: "資訊", zero: "無。", some: "僅供參考，無須處理。" },
];

/** 掃描模式（scan_mode）：文案照設計稿 L1976，括號裡直接講清楚會不會送出攻擊請求 */
const MODE_TEXT = { 0: "基本掃描（不對系統送出攻擊）", 1: "完整掃描（將對系統送出攻擊測試）" };

/* ===================== 小工具 ===================== */

/**
 * null 與 0 意思完全不同：0 是「跑完了、真的沒有」，null 是「後端這次沒有這個數字」。
 * 沒有的欄位整行不渲染（硬規則 10：沒有資料就不要假裝有），不要印成 0 或「—」。
 */
const has = (v) => v !== null && v !== undefined && v !== "";

const pct1 = (v) => `${Number(v).toFixed(1)}%`;

/**
 * 設計稿的時間寫法是「9 月 19 日 14:20」（L1960），不是 2026/9/19 14:20。
 * 跨年的紀錄補上年份，否則「1 月 3 日」看不出是今年還是去年的。
 * 只有這一頁這樣寫，所以不放進 design/domain.js（同 Layout.js 的 WEEKDAYS）。
 */
function fmtWhen(v) {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n) => String(n).padStart(2, "0");
  const year = d.getFullYear() === new Date().getFullYear() ? "" : `${d.getFullYear()} 年 `;
  return `${year}${d.getMonth() + 1} 月 ${d.getDate()} 日 ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * 有沒有這個模組的任何一項權限。
 *
 * token 的 permission claim 同時有模組碼（codescan）與動作碼（codescan.create），
 * 只比模組碼的話，某些角色只被授了動作碼就會整張卡看不到。
 * 真正的把關一律在後端的 [PermissionOnly]，這裡只決定要不要畫。
 */
function hasModule(code) {
  const prefix = `${code}.`;
  return actionPermissions().some((p) => {
    const s = String(p).toLowerCase();
    return s === code || s.startsWith(prefix);
  });
}

/** 受影響網址在列上只需要主機名稱，整串網址會把列擠爆 */
function hostOf(url) {
  const s = safeHttpUrl(url);
  if (!s) return String(url || "");
  try {
    return new URL(s).host;
  } catch {
    return s;
  }
}

/** SonarQube 品質門檻 → 一句話的結論 */
function gateTag(gate) {
  const g = String(gate || "").toUpperCase();
  if (g === "OK") return <StatusChip kind="done" mark={<MdCheck />}>通過</StatusChip>;
  if (g === "ERROR") return <StatusChip kind="attention" mark={<MdWarningAmber />}>未通過</StatusChip>;
  if (g === "WARN") return <StatusChip kind="warn" mark={<MdErrorOutline />}>警告</StatusChip>;
  return <StatusChip kind="closed" mark={<MdRemove />}>無門檻結果</StatusChip>;
}

/**
 * 一次批次的「結果」—— 歷史列右邊那一欄。
 *
 * 還在跑、失敗、取消的批次先講狀態；真的跑完了（status 2）才講結論：
 * 程式碼檢查看品質門檻，資安掃描看最嚴重的那一階有幾項。
 */
function runResultTag(src, r) {
  const st = Number(r.status);
  if (isActiveStatus(st)) return <StatusChip kind="progress" mark={<Ring size={0.9} />}>{st === 0 ? "排隊中" : "執行中"}</StatusChip>;
  if (st === 3) return <StatusChip kind="attention" mark={<MdWarningAmber />}>失敗</StatusChip>;
  if (st === 4) return <StatusChip kind="closed" mark={<MdRemove />}>已取消</StatusChip>;
  if (st === 5) return <StatusChip kind="warn" mark={<MdErrorOutline />}>部分失敗</StatusChip>;

  if (src === "code") return gateTag(r.quality_gate);

  if (Number(r.high_count) > 0) return <StatusChip kind="attention" mark={<MdWarningAmber />}>{fmtCount(r.high_count)} 項高風險</StatusChip>;
  if (Number(r.medium_count) > 0) return <StatusChip kind="progress" mark={<MdCircle size="0.6em" />}>{fmtCount(r.medium_count)} 項中風險</StatusChip>;
  if (has(r.high_count) && has(r.medium_count)) return <StatusChip kind="done" mark={<MdCheck />}>無待處理項目</StatusChip>;
  return <StatusChip kind="closed" mark={<MdRemove />}>完成</StatusChip>;
}

/**
 * 單一 repo 的結果（*_scan_result.status）。狀態碼跟整批不一樣：
 * 4 是「批次被取消時還沒輪到」＝已略過，不是已取消，兩個混用會讓人誤會是自己按了取消。
 */
function repoResultTag(r) {
  const st = Number(r.status);
  if (st === 0) return <StatusChip kind="closed" mark={<MdRemove />}>排隊中</StatusChip>;
  if (st === 1) return <StatusChip kind="progress" mark={<Ring size={0.9} />}>執行中</StatusChip>;
  if (st === 3) return <StatusChip kind="attention" mark={<MdWarningAmber />}>失敗</StatusChip>;
  if (st === 4) return <StatusChip kind="closed" mark={<MdRemove />}>已略過</StatusChip>;
  return gateTag(r.quality_gate);
}

/** 歷史列中間那一欄「關鍵數字」：只挑一個看得懂的，不要把整張表塞回來 */
function runKeyNumber(src, r) {
  if (src === "code") {
    if (has(r.coverage)) return `測試覆蓋率 ${pct1(r.coverage)}`;
    if (has(r.bugs)) return `錯誤 ${fmtCount(r.bugs)} 個`;
    if (has(r.success_count) && has(r.repo_count)) return `${fmtCount(r.success_count)}/${fmtCount(r.repo_count)} 個 repo`;
    return "";
  }
  /* 有哪幾個數字就寫哪幾個，湊不出來的那一半不要印成「—」 */
  const parts = [];
  if (has(r.high_count)) parts.push(`高 ${fmtCount(r.high_count)}`);
  if (has(r.medium_count)) parts.push(`中 ${fmtCount(r.medium_count)}`);
  if (parts.length > 0) return parts.join("、");
  if (has(r.success_count) && has(r.target_count)) return `${fmtCount(r.success_count)}/${fmtCount(r.target_count)} 個網址`;
  return "";
}

/* ===================== 重新掃描的確認視窗 ===================== */

/**
 * 資安掃描的二次確認（舊 VulnScan 的掃描模式下拉 ＋ confirmDelete 對話框）。
 *
 * 兩種模式各自把後果寫成一整句提示條 —— 完整掃描會真的對目標送出攻擊請求，
 * 這種事寫成灰色小字等於沒寫（硬規則 4）。Esc 與點背景關閉由 Dialog 處理。
 * 兩顆動作鈕都不是 contained：整頁只有「重新檢查一次」那一顆主要按鈕。
 */
function VulnRescanDialog({ projectName, busy, onCancel, onStart }) {
  return (
    <Dialog open onClose={onCancel} aria-labelledby="scan-rescan-title" fullWidth maxWidth="sm">
      <DialogTitle id="scan-rescan-title">重新掃描{projectName ? `：${projectName}` : ""}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>請選擇掃描方式。</Typography>
          <Banner kind="info" icon={<MdCircle size="0.6em" />} title="基本掃描">
            僅爬取網站並讀取回應內容，不會對系統送出攻擊請求，可安全用於線上服務。
          </Banner>
          <Banner kind="warn" icon={<MdWarningAmber />} title="完整掃描將實際送出攻擊請求">
            將對網站送出大量攻擊測試，可能留下測試資料、觸發警報或導致服務變慢。
            僅限掃描公司自有且已取得授權的系統，建議於測試環境執行。
          </Banner>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button variant="outlined" disabled={busy} onClick={() => onStart(0)}>
          開始基本掃描
        </Button>
        <Button variant="outlined" color="error" disabled={busy} onClick={() => onStart(1)}>
          開始完整掃描
        </Button>
        <Button variant="text" disabled={busy} onClick={onCancel}>
          取消
        </Button>
      </DialogActions>
    </Dialog>
  );
}


VulnRescanDialog.propTypes = {
  projectName: PropTypes.string,
  busy: PropTypes.bool,
  onCancel: PropTypes.func,
  onStart: PropTypes.func,
};

VulnRescanDialog.defaultProps = { projectName: "", busy: false, onCancel: () => {}, onStart: () => {} };

/* ===================== 明細抽屜 ===================== */

/**
 * 下鑽的明細：程式碼檢查看各個 repo，資安掃描看那一份弱點清單。
 *
 * 外殼直接用 ScanKit 既有的 ScanDrawer（portal、Esc、鎖背景捲動三個坑已經填好），
 * 裡面的每一列是整列可點的清單列（RowButton）而不是表格 —— 舊系統那張 10 欄的表格
 * 在窄螢幕會被切掉右邊，而弱點清單正是最需要看得完整的東西。
 *
 * 兩個 useRunReport 都要呼叫（hook 不能放進 if），不是這一次要看的那一邊傳 null，
 * hook 自己會略過請求也不會輪詢。
 *
 * 程式碼檢查的每一列原本是「按這一列到 SonarQube 看細節」，直接開 SonarQube 的網址 ——
 * 那是公司內網位址，使用者在外面按了打不開。問題清單其實早就存在 iProject 裡（IssuesJson），
 * 所以每一列改成連到 /CodeScan 的報告、直接捲到那個 repo 並打開它的問題清單；
 * 那裡可以下載成 CSV，SonarQube 的原始連結也還在，只是降成次要、並寫明是內網網址。
 * 資安掃描那一邊不用改：ZAP 的告警本來就存在 iProject 裡、在這裡就攤得開，沒有連到內網
 * （每一項的受影響網址 worker 只存前 5 個，列上已經寫了「列出 5 筆，共 N 處」）。
 */
function ScanReportDrawer({ src, runId, onClose }) {
  const codeReport = useRunReport(CODE_BASE, src === "code" ? runId : null);
  const vulnReport = useRunReport(VULN_BASE, src === "vuln" ? runId : null);
  const [openAlert, setOpenAlert] = useState(null);

  const { report, loading, error } = src === "code" ? codeReport : vulnReport;
  const run = report && report.run ? report.run : null;
  const proj = report && report.project ? report.project : null;
  /* useMemo 的相依要穩定：每次 render 生一個新的 [] 會讓下面的攤平重算一遍 */
  const results = useMemo(() => (report && Array.isArray(report.results) ? report.results : NO_RESULTS), [report]);

  /* 告警攤平成一份清單，依風險高 → 低、同風險再依件數多 → 少：最該先處理的永遠在最上面 */
  const alerts = useMemo(() => {
    if (src !== "vuln") return NO_RESULTS;
    const out = [];
    results.forEach((r) => {
      (Array.isArray(r.alerts) ? r.alerts : []).forEach((a) => out.push({ ...a, target: r.target_url }));
    });
    return out.sort((x, y) => Number(y.risk) - Number(x.risk) || Number(y.count) - Number(x.count));
  }, [src, results]);

  /* 這一頁只做「看結論、看明細」；取消、刪除、下載 HTML 報告都還在原本那支頁面 */
  const fullPage = `${src === "code" ? CODE_BASE : VULN_BASE}?projectId=${proj ? proj.id : ""}&run=${runId}`;

  /* 有幾個 repo 的清單比 SonarQube 統計的少、各是什麼原因 —— 只算檢測成功的，失敗的本來就沒有清單。
     原因分法跟 /CodeScan 報告抽屜共用 coverageBanner，兩邊的提示條才會講同一件事。 */
  const covBanner = useMemo(() => (src === "code" ? coverageBanner(results) : null), [src, results]);
  const hasSonarLink = src === "code" && results.some((r) => safeHttpUrl(r.dashboard_url));

  const moreLink = (
    <Box>
      <Button variant="text" component={Link} to={fullPage}>
        {src === "code" ? "檢視完整報告（各 repo 的問題與執行紀錄）" : "檢視完整報告（參考資料、下載 HTML 報告）"}
      </Button>
    </Box>
  );

  let body;
  if (error) {
    body = (
      <Banner kind="attention" icon={<MdWarningAmber />}>
        {error}
      </Banner>
    );
  } else if (!run) {
    body = loading ? <PageLoading dense /> : <Typography>找不到此報告。</Typography>;
  } else {
    const who = run.create_name || run.create_id || "";
    body = (
      <Stack spacing={2}>
        {run.error_message && (
          <Banner kind="attention" icon={<MdWarningAmber />}>
            {run.error_message}
          </Banner>
        )}
        <Stack spacing={1}>
          <Typography>排入時間：{fmtWhen(run.queued_at)}</Typography>
          {has(run.finished_at) && (
            <Typography>
              結束時間：{fmtWhen(run.finished_at)}（耗時 {fmtDuration(run.duration_seconds)}）
            </Typography>
          )}
          {who && <Typography>排入者：{who}</Typography>}
        </Stack>

        {/*
          清單不完整是後果（按進去看到的、下載的都不是全部），寫成提示條（硬規則 4）。
          只在真的有 repo 的清單比統計少時才出現；每一列的 meta 會寫是哪一個、少了多少。
          原因不一定是 100 筆上限（也可能是讀清單失敗），由 coverageBanner 分開講。
        */}
        {covBanner && (
          <Banner
            kind="warn"
            icon={<MdErrorOutline />}
            title={covBanner.sure ? "問題清單不完整" : "問題清單可能不完整"}
          >
            {covBanner.reasons.join("")}
            下方標示「問題清單僅儲存…」的 repo，其檢視的清單與下載的檔案僅包含已儲存的部分，其餘項目僅能於 SonarQube 查看。
          </Banner>
        )}

        {src === "code" && hasStoredIssues(results) && (
          <Stack spacing={1}>
            <Box>
              {/*
                不是 contained：整頁唯一的主要按鈕是「重新檢查一次」。
                允許換行：手機上抽屜太窄，一行放不下會撐出橫向捲軸。
              */}
              <Button
                variant="outlined"
                sx={{ whiteSpace: "normal" }}
                onClick={() => downloadIssuesCsv({ run, project: proj, results })}
              >
                下載本次問題清單（可用 Excel 開啟）
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              檔案開頭會列出各 repo 已儲存的筆數，以及是否完整。
            </Typography>
          </Stack>
        )}

        {src === "code" && results.length > 0 && (
          <Card>
            <List disablePadding>
              {results.map((r) => {
                const st = Number(r.status);
                const cov = issueCoverage(r);
                const bits = [r.branch || "預設分支"];
                if (has(r.coverage)) bits.push(`測試覆蓋率 ${pct1(r.coverage)}`);
                if (has(r.bugs)) bits.push(`錯誤 ${fmtCount(r.bugs)} 個`);
                if (has(r.vulnerabilities)) bits.push(`資安漏洞 ${fmtCount(r.vulnerabilities)} 個`);
                if (has(r.code_smells)) bits.push(`程式寫法問題 ${fmtCount(r.code_smells)} 個`);
                /*
                 * 清單存了幾筆直接寫在列上：Code Smell 寫 116 個、按進去只有 100 筆的話，
                 * 使用者要在按之前就知道，不是按進去才發現對不起來。
                 */
                if (st === 2) {
                  if (cov.stored === 0 && cov.total === 0) bits.push("無未解決的問題");
                  else if (cov.truncated && cov.total !== null) {
                    bits.push(`問題清單僅儲存 ${fmtCount(cov.stored)} 筆（SonarQube 共 ${fmtCount(cov.total)} 個）`);
                  } else if (cov.truncated) bits.push(`問題清單僅儲存前 ${fmtCount(cov.stored)} 筆`);
                  else bits.push(`問題清單 ${fmtCount(cov.stored)} 筆`);
                }
                bits.push(st === 2 ? "點選此列以檢視問題清單" : "點選此列以檢視該 repo 的結果");
                /* 連到站內的報告、指定 repo：/CodeScan 會捲到這一個並打開它的問題清單 */
                const to = `${CODE_BASE}?projectId=${proj ? proj.id : ""}&run=${runId}&repo=${r.id}`;
                return (
                  <RowButton
                    key={r.id}
                    component={Link}
                    to={to}
                    title={r.repo_url}
                    meta={bits.map((b) => (
                      <span key={b}>{b}</span>
                    ))}
                    side={repoResultTag(r)}
                  />
                );
              })}
            </List>
          </Card>
        )}
        {/*
          SonarQube 的連結沒有刪掉（在公司裡的人還用得到），只是不再是這一列唯一的去處：
          移到報告裡每個 repo 的問題清單旁邊。這裡講一聲它在哪、為什麼外面打不開。
        */}
        {hasSonarLink && (
          <Typography variant="body2" color="text.secondary">
            於公司內網仍可檢視 SonarQube 原始頁面：進入任一 repo 後，點選「至 SonarQube 檢視原始頁面」連結。
            該連結為公司內網網址，無法從外部網路開啟。
          </Typography>
        )}

        {src === "vuln" &&
          (alerts.length === 0 ? (
            <EmptyState title="本次未發現弱點" text="掃描完成，無任何告警。" />
          ) : (
            <Card>
              <List disablePadding>
                {alerts.map((a, i) => {
                  const risk = RISK_TAG[Number(a.risk)] ? Number(a.risk) : 0;
                  const label = RISK_LINES.find((f) => f.risk === risk).label;
                  const open = openAlert === i;
                  const desc = stripHtml(a.description);
                  const solution = stripHtml(a.solution);
                  const urls = Array.isArray(a.urls) ? a.urls.slice(0, 5) : [];
                  return (
                    /* 同一個 plugin_id 會在好幾個網址重覆出現，key 一定要帶位置才唯一 */
                    <React.Fragment key={`${a.plugin_id || "alert"}-${i}`}>
                      <RowButton
                        aria-expanded={open}
                        onClick={() => setOpenAlert(open ? null : i)}
                        chevron={false}
                        title={a.name}
                        meta={
                          <>
                            <span>{fmtCount(a.count)} 處</span>
                            <span>{hostOf(a.target)}</span>
                            <span>{open ? "收合說明" : "點選此列以檢視說明與修正方式"}</span>
                          </>
                        }
                        side={
                          <>
                            <StatusChip kind={RISK_TAG[risk]} mark={RISK_MARK[risk]}>
                              {label}
                            </StatusChip>
                            <Box
                              component="span"
                              aria-hidden="true"
                              sx={{ display: "flex", color: "text.secondary", fontSize: "var(--icon)" }}
                            >
                              {open ? <MdExpandMore /> : <MdChevronRight />}
                            </Box>
                          </>
                        }
                      />
                      {open && (
                        /* ZAP 的說明帶著換行，要保留 */
                        <Stack spacing={1} sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
                          {desc && <Typography sx={{ whiteSpace: "pre-wrap" }}>{desc}</Typography>}
                          {solution && (
                            <Typography sx={{ whiteSpace: "pre-wrap" }}>
                              <strong>修正方式　</strong>
                              {solution}
                            </Typography>
                          )}
                          {urls.length > 0 && (
                            <>
                              <Typography>
                                <strong>受影響的網址</strong>
                                {Number(a.count) > urls.length ? `（列出 ${urls.length} 筆，共 ${fmtCount(a.count)} 處）` : ""}
                              </Typography>
                              {/* 只列文字不做連結：裡面可能是會改資料的端點，誤點不該就送出請求 */}
                              {urls.map((u) => (
                                <Typography key={u}>
                                  <Typography
                                    component="code"
                                    sx={{ fontFamily: "var(--font-mono)", fontSize: "var(--t-code)", color: "text.secondary", wordBreak: "break-all" }}
                                  >
                                    {u}
                                  </Typography>
                                </Typography>
                              ))}
                            </>
                          )}
                        </Stack>
                      )}
                    </React.Fragment>
                  );
                })}
              </List>
            </Card>
          ))}

        {moreLink}
      </Stack>
    );
  }

  return (
    <ScanDrawer
      open
      onClose={onClose}
      title={src === "code" ? "本次程式碼檢查明細" : "本次資安掃描明細"}
      subtitle={proj ? [proj.code, proj.name].filter(Boolean).join(" · ") : ""}
    >
      {body}
    </ScanDrawer>
  );
}

ScanReportDrawer.propTypes = {
  src: PropTypes.oneOf(["code", "vuln"]).isRequired,
  runId: PropTypes.number.isRequired,
  onClose: PropTypes.func,
};

ScanReportDrawer.defaultProps = { onClose: () => {} };

/* ===================== 頁面本體 ===================== */

function ScanBoards({ canCode, canVuln }) {
  const { toast } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * 兩邊各呼叫一次 useScanPage（hook 不能寫在 if 裡）。沒有權限的那一邊改指向
   * 另一邊的 base —— 直接打 /CodeScan/Projects 會 403，hook 接著跳一個「載入專案
   * 清單失敗」的 toast，使用者會以為系統壞掉而不是自己沒被授權。
   * 兩邊同 base 時多打一次一樣的 GET，下面的聯集依 id 去重，畫面上看不出差別。
   */
  const codePage = useScanPage(canCode ? CODE_BASE : VULN_BASE);
  const vulnPage = useScanPage(canVuln ? VULN_BASE : CODE_BASE);

  const codeProjects = canCode ? codePage.projects : NO_PROJECTS;
  const vulnProjects = canVuln ? vulnPage.projects : NO_PROJECTS;
  const projectsLoaded = codePage.projectsLoaded && vulnPage.projectsLoaded;

  /*
   * 這個模組在後端存不存在。
   *
   * 實測這套後端的 Web/Controllers 底下沒有 CodeScanController 與 VulnScanController，
   * /CodeScan/Projects 與 /VulnScan/Projects 都直接回 404。專案清單因此永遠是空的，
   * 整頁掉進「你還看不到任何專案」的空狀態，兩張卡一張都畫不出來 ——
   * 而那句空狀態還叫使用者去找專案經理加專案，把「後端沒這支 API」講成「你沒權限」。
   *
   * 所以這裡把「打不通」單獨拉出來講，而且照講不誤：哪一邊、什麼狀態碼。
   */
  const codeDown = canCode ? codePage.unavailable : null;
  const vulnDown = canVuln ? vulnPage.unavailable : null;
  const bothDown = (!canCode || codeDown) && (!canVuln || vulnDown);

  /* 選哪個專案存在網址列（?projectId=），兩個 hook 讀的是同一個參數，用哪一邊都一樣 */
  const { projId, setProjId } = codePage;

  /** 兩邊 Projects 的聯集：一個專案可能只開了其中一個模組，少畫一邊就等於少一個入口 */
  const projects = useMemo(() => {
    const seen = new Set();
    return [...codeProjects, ...vulnProjects].filter((p) => {
      const key = String(p.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [codeProjects, vulnProjects]);

  const project = useMemo(() => projects.find((p) => String(p.id) === projId) || null, [projects, projId]);

  /*
   * 這個專案有沒有在各自模組的範圍裡。不在的那一邊傳 null，useRunHistory 就完全
   * 不會發請求 —— 拿 A 模組的專案 id 去打 B 模組的 Runs，回來的不是空就是 403。
   */
  const codePid = codeProjects.some((p) => String(p.id) === projId) ? Number(projId) : null;
  const vulnPid = vulnProjects.some((p) => String(p.id) === projId) ? Number(projId) : null;
  const codeRuns = useRunHistory(CODE_BASE, codePid);
  const vulnRuns = useRunHistory(VULN_BASE, vulnPid);

  /* 排入新批次要另外的動作權限；沒有的話連按鈕都不畫，按了只會 403 */
  const canRunCode = hasPermission("codescan.create");
  const canRunVuln = hasPermission("vulnscan.create");

  const [busy, setBusy] = useState("");
  const [rescanOpen, setRescanOpen] = useState(false);
  const [detail, setDetail] = useState(null); // { src, runId }；null = 沒有開抽屜

  const startCodeScan = async () => {
    if (!codePid || busy) return;
    setBusy("code");
    try {
      await apiPostJson(`${CODE_BASE}/Run`, { projectId: codePid });
      toast("已排入程式碼檢查，完成後將通知您");
      codeRuns.showFirstPage();
    } catch (e) {
      /* 還沒設定 Git 連結、已經有一次在跑…原因都由後端講，照原文轉出去比自己猜準 */
      toast(apiErrorMessage(e, "排入程式碼檢查失敗"), "error");
    } finally {
      setBusy("");
    }
  };

  const startVulnScan = async (mode) => {
    if (!vulnPid || busy) return;
    setBusy("vuln");
    try {
      await apiPostJson(`${VULN_BASE}/Run`, { projectId: vulnPid, mode });
      toast(`已排入${mode === 1 ? "完整" : "基本"}掃描，完成後將通知您`);
      setRescanOpen(false);
      vulnRuns.showFirstPage();
    } catch (e) {
      toast(apiErrorMessage(e, "排入資安掃描失敗"), "error");
    } finally {
      setBusy("");
    }
  };

  /* ---------- 程式碼檢查 ---------- */

  const renderCodeCard = () => {
    const latest = codeRuns.latest; // 最近一次真的有結果的批次
    const last = codeRuns.items[0] || null; // 最近一次，不論結果
    const gate = latest ? String(latest.quality_gate || "").toUpperCase() : "";

    let tag = null;
    if (codeRuns.hasActive) tag = <StatusChip kind="progress" mark={<MdCircle size="0.6em" />}>檢查中</StatusChip>;
    else if (gate === "OK") tag = <StatusChip kind="done" mark={<MdCheck />}>本次通過</StatusChip>;
    else if (gate === "ERROR") tag = <StatusChip kind="attention" mark={<MdWarningAmber />}>本次未通過</StatusChip>;
    else if (gate === "WARN") tag = <StatusChip kind="warn" mark={<MdErrorOutline />}>本次有警告</StatusChip>;
    else if (latest) tag = <StatusChip kind="closed" mark={<MdRemove />}>無品質門檻結果</StatusChip>;
    else if (last) tag = <StatusChip kind="attention" mark={<MdWarningAmber />}>上次檢查未完成</StatusChip>;

    /*
     * 沒過的原因寫成一整句（設計規則 L1942、硬規則 4）。
     *
     * 設計稿寫的是「測試覆蓋率 42%，低於公司標準的 60%」—— 42/60 都是範本假資料。
     * 覆蓋率從 API 來；「公司標準」後端沒有回，所以整句改成不含門檻的寫法，
     * 不自己編一個數字出來（硬規則 10）。
     */
    let reason = "";
    if (latest && (gate === "ERROR" || gate === "WARN")) {
      const bad = [];
      if (Number(latest.bugs) > 0) bad.push(`錯誤（Bug）${fmtCount(latest.bugs)} 個`);
      if (Number(latest.vulnerabilities) > 0) bad.push(`資安漏洞 ${fmtCount(latest.vulnerabilities)} 個`);
      if (Number(latest.security_hotspots) > 0) bad.push(`資安可疑點 ${fmtCount(latest.security_hotspots)} 個`);
      if (Number(latest.code_smells) > 0) bad.push(`程式寫法問題（Code Smell）${fmtCount(latest.code_smells)} 個`);
      const cover = has(latest.coverage) ? `測試覆蓋率 ${pct1(latest.coverage)}。` : "";
      const allZero = ["bugs", "vulnerabilities", "code_smells"].every(
        (k) => has(latest[k]) && Number(latest[k]) === 0
      );
      if (cover && bad.length > 0) reason = `${cover}另有 ${bad.join("、")}。`;
      else if (cover) reason = allZero ? `${cover}其他項目（錯誤、資安漏洞、程式寫法問題）皆為 0。` : cover;
      else if (bad.length > 0) reason = `${bad.join("、")}。`;
      else reason = "品質門檻判定未通過，但後端未提供未通過的項目；請開啟明細檢視各 repo 的結果。";
    }

    /* 數字逐行寫成句子，不做四格數字牆（設計規則 L1941）。沒有的欄位整行不畫 */
    const lines = [];
    if (latest) {
      if (has(latest.bugs)) lines.push(`錯誤（Bug）：${fmtCount(latest.bugs)} 個`);
      if (has(latest.vulnerabilities)) lines.push(`資安漏洞：${fmtCount(latest.vulnerabilities)} 個`);
      if (has(latest.code_smells)) lines.push(`程式寫法問題（Code Smell）：${fmtCount(latest.code_smells)} 個`);
      if (has(latest.security_hotspots)) lines.push(`資安可疑點（Security Hotspot）：${fmtCount(latest.security_hotspots)} 個`);
      if (has(latest.coverage)) lines.push(`測試覆蓋率：${pct1(latest.coverage)}`);
      if (has(latest.duplication)) lines.push(`重複的程式碼：${pct1(latest.duplication)}`);
      if (has(latest.ncloc)) lines.push(`程式行數：${fmtCount(latest.ncloc)} 行`);
    }

    const who = latest ? latest.create_name || latest.create_id || "" : "";

    return (
      <Card component="section">
        <CardContent>
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="h3" component="h2">
              程式碼檢查
            </Typography>
            {tag}
          </Stack>

          {codeRuns.failed && (
            <Banner kind="warn" icon={<MdWarningAmber />} sx={{ mt: 2 }}>
              檢查紀錄載入失敗，下方結果可能不是最新資料，請重新整理後再試。
            </Banner>
          )}

          {reason && (
            <Banner
              kind={gate === "ERROR" ? "attention" : "warn"}
              icon={<MdWarningAmber />}
              title={gate === "ERROR" ? "未通過原因" : "警告原因"}
              sx={{ mt: 2 }}
            >
              {reason}
            </Banner>
          )}

          {codeRuns.hasActive && <Typography sx={{ mt: 2 }}>檢查中，此區每 5 秒自動更新，無須手動重新整理。</Typography>}

          {/* 還在載入時不要講「還沒有檢查過」—— 那是還不知道，不是真的沒有 */}
          {!latest && !codeRuns.hasActive && (
            <Typography sx={{ mt: 2 }}>
              {(() => {
                if (codeRuns.loading && codeRuns.items.length === 0) return <InlineLoading />;
                if (last) return "最近一次檢查未完成，可至「歷史檢測紀錄」分頁查看中斷位置。";
                return "此專案尚未完成任何程式碼檢查。首次使用前，請先設定要檢查的 Git repo。";
              })()}
            </Typography>
          )}

          {lines.length > 0 && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              {lines.map((t) => (
                <Typography key={t}>{t}</Typography>
              ))}
            </Stack>
          )}

          {/* 沒有權限又還沒跑過時整列都不畫（設計規則 2：空的區塊不要畫出來） */}
          {(canRunCode || latest) && (
            <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 3 }}>
              {/* 整頁唯一一顆 contained 主要按鈕（設計規則 1） */}
              {canRunCode && (
                <Button
                  variant="contained"
                  disabled={Boolean(busy) || codeRuns.hasActive}
                  loading={busy === "code"}
                  loadingPosition="start"
                  onClick={startCodeScan}
                >
                  重新檢查
                </Button>
              )}
              {latest && (
                <Button variant="text" onClick={() => setDetail({ src: "code", runId: latest.id })}>
                  檢視本次明細
                </Button>
              )}
              {latest && (
                <Typography component="span" variant="body2" color="text.secondary">
                  上次檢查：{fmtWhen(latest.finished_at || latest.queued_at)}
                  {who ? ` · ${who}` : ""}
                </Typography>
              )}
            </Stack>
          )}
          {canRunCode && codeRuns.hasActive && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              此專案已有檢查正在執行，須待其結束後才能再次排入。
            </Typography>
          )}
          {/* 維護 Git 連結留在原本那支頁面（功能一個都沒拿掉），這裡只留進得去的入口 */}
          <Box sx={{ mt: 2 }}>
            <Button variant="text" component={Link} to={`/CodeScan?projectId=${projId}`}>
              設定檢查的 Git repo
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  /* ---------- 資安掃描 ---------- */

  const renderVulnCard = () => {
    const latest = vulnRuns.latest;
    const last = vulnRuns.items[0] || null;

    let tag = null;
    if (vulnRuns.hasActive) tag = <StatusChip kind="progress" mark={<MdCircle size="0.6em" />}>掃描中</StatusChip>;
    else if (latest && Number(latest.high_count) > 0) {
      tag = (
        <StatusChip kind="attention" mark={<MdWarningAmber />}>
          有 {fmtCount(latest.high_count)} 項高風險
        </StatusChip>
      );
    } else if (latest && Number(latest.medium_count) > 0) {
      tag = (
        <StatusChip kind="progress" mark={<MdCircle size="0.6em" />}>
          有 {fmtCount(latest.medium_count)} 項待處理
        </StatusChip>
      );
    } else if (latest && has(latest.high_count) && has(latest.medium_count)) {
      tag = <StatusChip kind="done" mark={<MdCheck />}>無待處理項目</StatusChip>;
    } else if (latest) tag = <StatusChip kind="closed" mark={<MdRemove />}>無風險統計</StatusChip>;
    else if (last) tag = <StatusChip kind="attention" mark={<MdWarningAmber />}>上次掃描未完成</StatusChip>;

    const lines = latest ? RISK_LINES.filter((f) => has(latest[f.key])) : [];

    /*
     * 下鑽入口 —— 設計稿 L1984 寫的是「看 16 項中風險」（那份範例剛好沒有高風險）。
     * 這裡改成挑「最嚴重且不是 0」的那一階：有高風險時還叫人去看中風險就本末倒置了。
     * 這顆是 --secondary：整頁的 --primary 留給「重新檢查一次」。
     */
    const drill = latest ? RISK_LINES.filter((f) => f.risk > 0).find((f) => Number(latest[f.key]) > 0) : null;
    const mode = latest && has(latest.scan_mode) ? MODE_TEXT[Number(latest.scan_mode)] : "";
    const who = latest ? latest.create_name || latest.create_id || "" : "";

    return (
      <Card component="section">
        <CardContent>
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="h3" component="h2">
              資安掃描
            </Typography>
            {tag}
          </Stack>

          {vulnRuns.failed && (
            <Banner kind="warn" icon={<MdWarningAmber />} sx={{ mt: 2 }}>
              掃描紀錄載入失敗，下方結果可能不是最新資料，請重新整理後再試。
            </Banner>
          )}

          {latest && Number(latest.high_count) > 0 && (
            <Banner kind="attention" icon={<MdWarningAmber />} title="有高風險項目須優先處理" sx={{ mt: 2 }}>
              本次掃描發現 {fmtCount(latest.high_count)} 項高風險，此類通常為可直接被利用的漏洞，請勿延至下個版本處理。
            </Banner>
          )}

          {vulnRuns.hasActive && <Typography sx={{ mt: 2 }}>掃描中，此區每 5 秒自動更新，無須手動重新整理。</Typography>}

          {/* 還在載入時不要講「還沒有掃描過」—— 那是還不知道，不是真的沒有 */}
          {!latest && !vulnRuns.hasActive && (
            <Typography sx={{ mt: 2 }}>
              {(() => {
                if (vulnRuns.loading && vulnRuns.items.length === 0) return <InlineLoading />;
                if (last) return "最近一次掃描未完成，可至「歷史檢測紀錄」分頁查看中斷位置。";
                return "此專案尚未完成任何資安掃描。首次使用前，請先設定要掃描的網址。";
              })()}
            </Typography>
          )}

          {lines.length > 0 && (
            <Stack spacing={1} sx={{ mt: 2 }}>
              {lines.map((f) => (
                <Typography key={f.key}>
                  <strong>
                    {f.label} {fmtCount(latest[f.key])} 項
                  </strong>
                  　{Number(latest[f.key]) > 0 ? f.some : f.zero}
                </Typography>
              ))}
            </Stack>
          )}

          {/* 沒有權限又還沒掃過時整列都不畫（設計規則 2：空的區塊不要畫出來） */}
          {(canRunVuln || latest) && (
            <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 3 }}>
              {latest && (
                <Button variant="outlined" onClick={() => setDetail({ src: "vuln", runId: latest.id })}>
                  {drill ? `檢視 ${fmtCount(latest[drill.key])} 項${drill.label}` : "檢視本次明細"}
                </Button>
              )}
              {canRunVuln && (
                <Button variant="text" disabled={Boolean(busy) || vulnRuns.hasActive} onClick={() => setRescanOpen(true)}>
                  重新掃描
                </Button>
              )}
              {latest && (
                <Typography component="span" variant="body2" color="text.secondary">
                  上次掃描：{fmtWhen(latest.finished_at || latest.queued_at)}
                  {mode ? ` · ${mode}` : ""}
                  {who ? ` · ${who}` : ""}
                </Typography>
              )}
            </Stack>
          )}
          {canRunVuln && vulnRuns.hasActive && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              此專案已有掃描正在執行，須待其結束後才能再次排入。
            </Typography>
          )}
          {/* 維護掃描網址留在原本那支頁面（功能一個都沒拿掉），這裡只留進得去的入口 */}
          <Box sx={{ mt: 2 }}>
            <Button variant="text" component={Link} to={`/VulnScan?projectId=${projId}`}>
              設定掃描網址
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  /* ---------- 歷史 ---------- */

  /**
   * 兩邊的第一頁交錯成一份時間序。
   *
   * 只留 4 欄：時間、誰跑的、關鍵數字、結果（設計規則 L2003）。舊系統一邊 10 欄
   * 一邊 11 欄，窄螢幕直接被切掉右邊。每一列多帶一個來源標記，不然合在一起就分不出
   * 這一次是程式碼檢查還是資安掃描。
   */
  const history = useMemo(() => {
    const rows = [];
    codeRuns.items.forEach((r) => rows.push({ src: "code", label: "程式碼檢查", run: r }));
    vulnRuns.items.forEach((r) => rows.push({ src: "vuln", label: "資安掃描", run: r }));
    return rows.sort((a, b) => new Date(b.run.queued_at) - new Date(a.run.queued_at));
  }, [codeRuns.items, vulnRuns.items]);

  /*
   * 原本收在 <details> 收合區裡；改成分頁之後分頁本身就是「收起來」，
   * 再包一層收合等於要按兩次才看得到，所以直接攤開。
   */
  const renderHistory = () => {
    const total = codeRuns.total + vulnRuns.total;
    return (
      <Stack spacing={2}>
        <Card>
          <List disablePadding>
            {history.map(({ src, label, run }) => {
              const who = run.create_name || run.create_id || "";
              const key = runKeyNumber(src, run);
              return (
                <RowButton
                  key={`${src}-${run.id}`}
                  onClick={() => setDetail({ src, runId: run.id })}
                  title={fmtWhen(run.queued_at)}
                  meta={
                    <>
                      <span>{label}</span>
                      {who && <span>{who}</span>}
                      {key && <span>{key}</span>}
                    </>
                  }
                  side={runResultTag(src, run)}
                />
              );
            })}
          </List>
        </Card>
        {history.length < total && (
          <Typography variant="body2" color="text.secondary">
            此處僅列出近期紀錄。如需檢視全部紀錄、取消執行中的檢測或刪除紀錄，請至下方的完整頁面。
          </Typography>
        )}
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
          {canCode && (
            <Button variant="text" component={Link} to={`/CodeScan?projectId=${projId}`}>
              程式碼檢查完整頁面
            </Button>
          )}
          {canVuln && (
            <Button variant="text" component={Link} to={`/VulnScan?projectId=${projId}`}>
              資安掃描完整頁面
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  /* ---------- 分頁 ---------- */

  /*
   * 分頁標籤上的警訊：切到別的分頁時，這一頁的「沒過」「高風險」提示條就看不到了，
   * 所以把結論縮成一顆標籤掛在分頁上。只掛需要注意的狀態 —— 每個分頁都掛一顆「通過」
   * 只會讓真正的警訊被淹沒。判斷跟卡片裡的標籤同一套，不另外發明。
   */
  const codeGate = codeRuns.latest ? String(codeRuns.latest.quality_gate || "").toUpperCase() : "";
  let codeNote = null;
  if (codeRuns.hasActive) codeNote = <StatusChip kind="progress" mark={<MdCircle size="0.6em" />}>檢查中</StatusChip>;
  else if (codeGate === "ERROR") codeNote = <StatusChip kind="attention" mark={<MdWarningAmber />}>未通過</StatusChip>;
  else if (codeGate === "WARN") codeNote = <StatusChip kind="warn" mark={<MdErrorOutline />}>有警告</StatusChip>;

  const vulnHigh = vulnRuns.latest ? Number(vulnRuns.latest.high_count) || 0 : 0;
  let vulnNote = null;
  if (vulnRuns.hasActive) vulnNote = <StatusChip kind="progress" mark={<MdCircle size="0.6em" />}>掃描中</StatusChip>;
  else if (vulnHigh > 0) vulnNote = <StatusChip kind="attention" mark={<MdWarningAmber />}>{fmtCount(vulnHigh)} 項高風險</StatusChip>;

  /*
   * 只畫有內容的分頁（設計規則 2）。codePid／vulnPid 在沒有權限時一定是 null
   * （沒權限那一邊的專案清單是空的），所以沒權限的分頁自然不會出現。
   */
  const tabs = [];
  if (codePid) tabs.push({ key: TAB_CODE, label: "程式碼檢查", note: codeNote, render: renderCodeCard });
  if (vulnPid) tabs.push({ key: TAB_VULN, label: "資安掃描", note: vulnNote, render: renderVulnCard });
  if (history.length > 0) {
    tabs.push({
      key: TAB_HISTORY,
      label: `歷史檢測紀錄（${codeRuns.total + vulnRuns.total} 次）`,
      note: null,
      render: renderHistory,
    });
  }

  /*
   * 選哪個分頁記在網址列（?tab=，同 Approval/Index.js）：從明細連到 /CodeScan 再按上一頁回來時，
   * 停在原本那個分頁，不會被丟回第一頁。網址上的分頁現在不存在（換了專案、沒權限）就退回第一個。
   */
  const urlTab = searchParams.get("tab");
  let activeTab = tabs.length > 0 ? tabs[0].key : "";
  if (tabs.some((t) => t.key === urlTab)) activeTab = urlTab;

  const selectTab = useCallback(
    (key) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", key);
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  const renderTabs = () => {
    const current = tabs.find((t) => t.key === activeTab);
    /* 只剩一個分頁時不畫分頁列：一顆沒得切換的分頁只是多一條線，卡片自己就有標題 */
    if (tabs.length === 1) return current.render();
    return (
      <>
        {/* 三個分頁在手機或大字模式一行放不下，用可捲動的 Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_e, key) => selectTab(key)}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="檢測項目"
        >
          {tabs.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              id={`scan-tab-${t.key}`}
              aria-controls="scan-panel"
              label={
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                  {t.label}
                  {t.note}
                </Box>
              }
            />
          ))}
        </Tabs>
        <Box id="scan-panel" role="tabpanel" aria-labelledby={`scan-tab-${activeTab}`} sx={{ mt: 3 }}>
          {current.render()}
        </Box>
      </>
    );
  };

  /* ---------- 版面 ---------- */

  /*
   * 外層不再包一層頁面容器：Layout 已經把頁面的內距與寬度上限畫好了，
   * 這裡再包一次等於兩份內距與兩層上限。
   */
  const main = () => {
    /* 兩邊都打不通：講清楚是後端沒有這個模組，不要叫人去要權限 */
    if (bothDown) {
      return (
        <Stack spacing={2}>
          <Banner kind="attention" icon={<MdWarningAmber />} title="檢測服務目前無法連線" role="alert">
            程式碼檢查與資安掃描的 API 皆回傳 404，表示後端尚未部署這兩個模組。
            此問題與您的權限或專案設定無關，待後端部署完成後，本頁即會顯示資料。
          </Banner>
          <Typography variant="body2" color="text.secondary">
            無法連線的服務：
            {[codeDown ? `程式碼檢查（${codeDown.status || "無回應"}）` : null,
              vulnDown ? `資安掃描（${vulnDown.status || "無回應"}）` : null]
              .filter(Boolean).join("、")}
          </Typography>
        </Stack>
      );
    }
    if (!projId) {
      return projectsLoaded && projects.length === 0 ? (
        <EmptyState title="目前沒有可檢視的專案" text="請專案經理將您加入專案成員，加入後此處將顯示可檢測的專案。" />
      ) : (
        <EmptyState title="請選擇專案" text="選擇後，此處將顯示該專案的程式碼檢查與資安掃描結果。" />
      );
    }
    if (!project) {
      return projectsLoaded ? (
        <EmptyState title="找不到此專案" text="專案可能已刪除，或不在您的檢視範圍內。" />
      ) : (
        <PageLoading />
      );
    }
    if (!codePid && !vulnPid) {
      return (
        <EmptyState
          title="此專案未啟用檢測"
          text="此專案不在程式碼檢查與資安掃描的範圍內。請選擇其他專案，或請專案經理將其加入。"
        />
      );
    }
    /* 走到這裡 codePid 或 vulnPid 至少有一個，所以至少有一個分頁 */
    return renderTabs();
  };

  return (
    <>
      <Typography variant="h1" component="h1">
        檢測
      </Typography>
      <Box sx={{ mt: 1.5 }}>
        <TextField
          select
          id="scan-project"
          label="專案"
          value={projId}
          onChange={(e) => setProjId(e.target.value)}
          slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
          sx={{ minWidth: 240, maxWidth: "100%" }}
        >
          <option value="">請選擇專案</option>
          {projects.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.code ? `${p.code} ${p.name}` : p.name}
            </option>
          ))}
        </TextField>
      </Box>

      <Box sx={{ mt: 3 }}>{main()}</Box>

      {rescanOpen && (
        <VulnRescanDialog
          projectName={project ? project.name : ""}
          busy={busy === "vuln"}
          onCancel={() => setRescanOpen(false)}
          onStart={startVulnScan}
        />
      )}
      {detail && <ScanReportDrawer src={detail.src} runId={detail.runId} onClose={() => setDetail(null)} />}
    </>
  );
}

ScanBoards.propTypes = { canCode: PropTypes.bool.isRequired, canVuln: PropTypes.bool.isRequired };

/**
 * 權限的判斷刻意放在最外層、而且放在所有 hook 之前：
 * 兩個模組都沒有權限時，連 useScanPage 都不要掛上去，才不會打兩支必定 403 的 API。
 */
function ScanIndex() {
  const canCode = hasModule("codescan");
  const canVuln = hasModule("vulnscan");

  if (!canCode && !canVuln) {
    return (
      <>
        <Typography variant="h1" component="h1">
          檢測
        </Typography>
        <EmptyState
          title="您沒有檢測權限"
          text="程式碼檢查與資安掃描為分別授權的模組，您目前皆未獲授權。如需使用，請聯絡系統管理員開通。"
        />
      </>
    );
  }

  return <ScanBoards canCode={canCode} canVuln={canVuln} />;
}

export default ScanIndex;
